import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API Configuration
const API_BASE_URL ='https://git-hub-crawler-ul11.vercel.app/';

// API Response Types
export interface Repository {
  id: string;
  name_with_owner: string;
  stargazer_count: number;
  created_at: string;
  fetched_at: string;
}

export interface InsightItem {
  repository_name: string;
  trend_description?: string;
  ai_explanation?: string;
}

export interface InsightsResponse {
  items: InsightItem[];
}

export interface CrawlResponse {
  status: string;
  target: number;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  size: number;
  total?: number;
}

// API Service Class
class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add any auth tokens here if needed
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        // Handle common errors
        if (error.response?.status === 404) {
          console.error('Resource not found');
        } else if (error.response?.status === 500) {
          console.error('Server error occurred');
        } else if (error.response?.status === 429) {
          console.error('Rate limit exceeded');
        }
        return Promise.reject(error);
      }
    );
  }

  // Health Check
  async getHealth(): Promise<HealthResponse> {
    const response = await this.client.get<HealthResponse>('/health');
    return response.data;
  }

  // Repository Operations
  async getRepositories(page: number = 1, size: number = 50): Promise<Repository[]> {
    const response = await this.client.get<Repository[]>('/repos', {
      params: { page, size },
    });
    return response.data;
  }

  // Crawling Operations
  async startCrawl(): Promise<CrawlResponse> {
    const response = await this.client.post<CrawlResponse>('/crawl/start');
    return response.data;
  }

  // Analytics
  async getTrendingInsights(): Promise<InsightsResponse> {
    const response = await this.client.get<InsightsResponse>('/analytics/trending');
    return response.data;
  }

  // Utility method for retry logic
  async retryRequest<T>(
    request: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: unknown;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await request();
      } catch (error) {
        lastError = error;
        
        if (attempt < maxRetries - 1) {
          // Wait before retrying with exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
        }
      }
    }
    
    if (lastError instanceof Error) {
      throw lastError;
    }
    throw new Error(String(lastError ?? 'Unknown error'));
  }

  // Method to check if API is available
  async isApiAvailable(): Promise<boolean> {
    try {
      await this.getHealth();
      return true;
    } catch {
      return false;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;